import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Mail, 
  Send, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  UserPlus,
  PartyPopper,
  Smartphone,
  FileText,
  MailCheck,
  FileOutput,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const EmailTestCard = ({ emailType, name, description, trigger, icon: Icon, onSend, sending, isDark }) => {
  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
            <Icon className="h-6 w-6 text-[#ffd108]" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{name}</h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{description}</p>
            <div className="mt-2">
              <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-400' : 'border-gray-300 text-gray-500'}>
                Trigger: {trigger}
              </Badge>
            </div>
          </div>
          <Button 
            onClick={() => onSend(emailType)}
            disabled={sending === emailType}
            className="btn-volt"
          >
            {sending === emailType ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SuperAdminEmails = () => {
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [emailTypes, setEmailTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [testEmail, setTestEmail] = useState(admin?.email || '');

  useEffect(() => {
    fetchEmailTypes();
    if (admin?.email) {
      setTestEmail(admin.email);
    }
  }, [admin]);

  const fetchEmailTypes = async () => {
    try {
      const res = await api.get('/api/super-admin/emails/types');
      setEmailTypes(res.data.email_types || []);
    } catch (err) {
      console.error('Failed to fetch email types:', err);
      toast.error('Failed to load email types');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendTest = async (emailType) => {
    if (!testEmail || !validateEmail(testEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(emailType);
    try {
      const res = await api.post('/api/super-admin/emails/test', {
        email_type: emailType,
        to_email: testEmail
      });
      
      setLastSent({
        type: emailType,
        email: res.data.sent_to,
        time: new Date().toLocaleTimeString()
      });
      
      toast.success(`Test email sent to ${res.data.sent_to}`, {
        description: `Check inbox for the ${res.data.email_type} email`
      });
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error('Failed to send test email', {
        description: err.response?.data?.detail || 'Please check Resend configuration'
      });
    } finally {
      setSending(null);
    }
  };

  const getIconForType = (typeId) => {
    switch (typeId) {
      case 'welcome':
        return PartyPopper;
      case 'user_invitation':
        return UserPlus;
      case 'operator_mobile':
        return Smartphone;
      case 'quote_request_sales':
        return FileText;
      case 'quote_confirmation':
        return MailCheck;
      case 'docket':
        return FileOutput;
      case 'super_admin_invitation':
        return ShieldCheck;
      default:
        return Mail;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Testing</h1>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
          Send test emails to verify templates and delivery
        </p>
      </div>

      {/* Email Input Card */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="test-email" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                Send test emails to
              </Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address"
                className={`mt-1.5 ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <Badge variant="outline" className={`h-9 px-3 ${isDark ? 'border-slate-600 text-slate-400' : 'border-gray-300 text-gray-500'}`}>
              <Mail className="h-3 w-3 mr-1.5" />
              [TEST] prefix added
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Last Sent Notification */}
      {lastSent && (
        <Card className={isDark ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                Last sent: <strong>{lastSent.type}</strong> email to {lastSent.email} at {lastSent.time}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Type Cards */}
      <div className="space-y-4">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Available Email Templates</h2>
        
        {emailTypes.map((emailType) => (
          <EmailTestCard
            key={emailType.id}
            emailType={emailType.id}
            name={emailType.name}
            description={emailType.description}
            trigger={emailType.trigger}
            icon={getIconForType(emailType.id)}
            onSend={handleSendTest}
            sending={sending}
            isDark={isDark}
          />
        ))}
      </div>

      {/* Help Text */}
      <Card className={isDark ? 'bg-slate-800/30 border-slate-700' : 'bg-gray-50 border-gray-200'}>
        <CardHeader>
          <CardTitle className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>About These Emails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <PartyPopper className="h-4 w-4 mt-0.5 text-[#ffd108]" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-slate-300' : 'text-gray-700'}>Welcome Email:</strong> Automatically sent after successful Stripe checkout when a new tenant is created.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <UserPlus className="h-4 w-4 mt-0.5 text-[#ffd108]" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-slate-300' : 'text-gray-700'}>User Invitation:</strong> Sent when an admin adds a new user (admin/dispatcher) to their tenant. Contains a secure link to set their password.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Smartphone className="h-4 w-4 mt-0.5 text-[#ffd108]" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-slate-300' : 'text-gray-700'}>Operator Mobile Access:</strong> Sent when an operator is enabled for mobile portal access. Contains their operator code and PIN.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <FileOutput className="h-4 w-4 mt-0.5 text-[#ffd108]" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-slate-300' : 'text-gray-700'}>Docket Email:</strong> Sends a professional PDF docket to the customer. Includes all job details, work items, and material loads.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-[#ffd108]" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              <strong className={isDark ? 'text-slate-300' : 'text-gray-700'}>Super Admin Invitation:</strong> Sent when inviting a new support agent to join the EQUIPR CORE admin team. Contains a secure link to set their password.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminEmails;
